import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Tooltip, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

const dotColours = {
  LOADED: '#2EAA4A',
  LOADING: '#E2A93B',
  FAILED: '#D3462C',
  IDLE: '#D0CDC8',
};

const getPagesLabel = (pages) => {
  return translate(pages === 1 ? 'case.Monitor.Sources.Page' : 'case.Monitor.Sources.Pages', {count: pages});
};

const SourceItem = (props) => {
  const {
    status,
    titleExpression,
    scope,
    detail,
    tooltip,
  } = props;

  const content = (
    <Stack direction='row' spacing={0.75} alignItems='center'>
      <Box sx={{width: 7, height: 7, borderRadius: '50%', backgroundColor: dotColours[status] || dotColours.IDLE}} />
      <Typography sx={{fontSize: '12px', fontWeight: 600, color: '#1A1916', whiteSpace: 'nowrap'}}>
        <Translate expression={titleExpression} />
      </Typography>
      <Typography sx={{fontSize: '12px', color: '#A8A49E', whiteSpace: 'nowrap'}}>
        {scope}
      </Typography>
      <Typography
        sx={{
          fontSize: '11px',
          fontFamily: 'monospace',
          color: status === 'FAILED' ? '#D3462C' : '#6D7280',
          whiteSpace: 'nowrap',
        }}
      >
        {detail}
      </Typography>
    </Stack>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip title={tooltip}>
      {content}
    </Tooltip>
  );
};

SourceItem.propTypes = {
  status: PropTypes.string.isRequired,
  titleExpression: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
};

const getPagedDetail = (source) => {
  if (source.status === 'IDLE') {
    return translate('case.Monitor.Sources.NotLoaded');
  }

  if (source.status === 'FAILED') {
    return translate('case.Monitor.Sources.Failed');
  }

  if (source.status === 'LOADING' && !source.pages) {
    return translate('case.Monitor.Sources.Loading');
  }

  return getPagesLabel(source.pages);
};

const getJobsDetail = (jobsSource) => {
  if (jobsSource.status === 'FAILED') {
    return translate('case.Monitor.Sources.Failed');
  }

  if (jobsSource.status !== 'LOADED') {
    return translate('case.Monitor.Sources.Loading');
  }

  return `${translate('case.Monitor.Sources.Jobs.Scope')} · ${translate('case.Monitor.Sources.Jobs.Count', {count: jobsSource.jobCount})}`;
};

const getJobsTooltip = (jobsSource) => {
  if (jobsSource.status === 'FAILED') {
    return translate('case.Monitor.Sources.Jobs.FailedDetail');
  }

  if (jobsSource.status !== 'LOADED') {
    return '';
  }

  return translate('case.Monitor.Sources.Jobs.Detail', {
    fetched: jobsSource.fetchedCount,
    skipped: jobsSource.skippedCount,
  });
};

const SourceStrip = (props) => {
  const {
    openPendingSource,
    jobsSource,
    markedSource,
    completedSource,
    rangeKey,
  } = props;

  const openPendingScope = rangeKey === 'ALL_TIME' ?
    translate('case.Monitor.Sources.OpenPending.ScopeAllTime') :
    translate('case.Monitor.Sources.OpenPending.Scope', {window: translate(`case.Monitor.Window.${rangeKey}`)});
  const markedScope = rangeKey === 'ALL_TIME' ?
    translate('case.Monitor.Sources.Marked.ScopeAllTime') :
    translate('case.Monitor.Sources.Marked.Scope', {window: translate(`case.Monitor.Window.${rangeKey}`)});

  return (
    <Stack
      direction='row'
      spacing={3}
      alignItems='center'
      flexWrap='wrap'
      useFlexGap
      sx={{
        px: 3,
        py: 1,
        backgroundColor: '#F9F8F6',
        borderTop: '1px solid #ECECF1',
        borderBottom: '1px solid #ECECF1',
      }}
    >
      <SourceItem
        status={openPendingSource.status}
        titleExpression='case.Monitor.Sources.OpenPending.Title'
        scope={openPendingScope}
        detail={getPagedDetail(openPendingSource)}
      />
      <SourceItem
        status={jobsSource.status}
        titleExpression='case.Monitor.Sources.Jobs.Title'
        scope=''
        detail={getJobsDetail(jobsSource)}
        tooltip={getJobsTooltip(jobsSource)}
      />
      <SourceItem
        status={markedSource.status}
        titleExpression='case.Monitor.Sources.Marked.Title'
        scope={markedScope}
        detail={getPagedDetail(markedSource)}
      />
      <SourceItem
        status={completedSource.status}
        titleExpression='case.Monitor.Sources.Completed.Title'
        scope={translate(`case.Monitor.Window.${rangeKey}`)}
        detail={getPagedDetail(completedSource)}
      />
    </Stack>
  );
};

SourceStrip.propTypes = {
  openPendingSource: PropTypes.object.isRequired,
  jobsSource: PropTypes.object.isRequired,
  markedSource: PropTypes.object.isRequired,
  completedSource: PropTypes.object.isRequired,
  rangeKey: PropTypes.string.isRequired,
};

export default React.memo(SourceStrip);
